import { useSyncExternalStore } from "react";

import {
  forgetRememberedKey,
  type LockedRecord,
  type Protection,
  type ProtectionKind,
  type ReadOutcome,
  readRememberedKey,
  rememberKey,
  type SaveOutcome,
  type UnlockOutcome,
  unlockRememberedKey,
  type VaultFailure,
  type WipeOutcome,
} from "./langsmithKeyVault";

/**
 * The LangSmith key an admin types into the behaviour page (GEN5-5344).
 *
 * The key in use lives in this module's memory: never in the store (so not in
 * RTK Query's cache keys or devtools), never in a URL, a cookie, plain-text
 * storage or a log. A request reads it when it is sent and puts it in a
 * header; a request's cache key carries only the epoch below, so another key
 * refetches without the key being an argument.
 *
 * Opt-in (K2), the key is also kept on this device, encrypted
 * (`langsmithKeyVault.ts`). Where it is and what state it is in is one state
 * machine, `nextKeyState`: every move is written there, any other is refused,
 * and the answer of an operation that was overtaken (a Forget while saving)
 * is dropped by its operation number.
 */
export type LangSmithKey = {
  apiKey: string;
  /** One of LangSmith's two hosts, or null for cerebro's default. */
  apiUrl: string | null;
  /** A personal key reads only its default workspace; a UUID here reads another. Null: the key's default. */
  workspaceId: string | null;
};

const WORKSPACE_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether text is shaped like a LangSmith workspace id (a UUID), as cerebro requires. */
export const isWorkspaceId = (value: string): boolean =>
  WORKSPACE_ID_PATTERN.test(value.trim());

export const LANGSMITH_HOSTS = [
  "https://api.smith.langchain.com",
  "https://eu.api.smith.langchain.com",
] as const;

/** The header names that carry the key and its target: also what error reporting strips. */
export const LANGSMITH_HEADER_NAMES = [
  "X-LangSmith-Api-Key",
  "X-LangSmith-Api-Url",
  "X-LangSmith-Workspace-Id",
] as const;

// ------------------------------------------------------------ the machine

export type KeyState =
  /** The tab has not looked for a remembered key yet. */
  | { status: "unchecked" }
  | {
      status: "working";
      task: "checking" | "forgetting";
      operation: number;
    }
  | {
      status: "working";
      task: "saving";
      operation: number;
      key: LangSmithKey;
      protection: ProtectionKind;
    }
  /** The key is in use already; any copy on the device is being removed. */
  | {
      status: "working";
      task: "keepingInTab";
      operation: number;
      key: LangSmithKey;
    }
  | {
      status: "working";
      task: "unlocking";
      operation: number;
      record: LockedRecord;
      wrongAttempts: number;
    }
  /** `notice`: the device copy could not be removed on Forget. */
  | { status: "notSet"; notice: VaultFailure | null }
  /** `notice`: why it is not remembered although that was asked, or why a device copy could not be removed. */
  | { status: "tabOnly"; key: LangSmithKey; notice: VaultFailure | null }
  | { status: "remembered"; key: LangSmithKey; protection: ProtectionKind }
  | { status: "locked"; record: LockedRecord; wrongAttempts: number }
  | { status: "unreadable"; failure: VaultFailure }
  /** This browser cannot remember a key at all; a typed key still works in this tab. */
  | { status: "unavailable"; key: LangSmithKey | null; failure: VaultFailure };

export type KeyEvent =
  | { type: "checkStarted"; operation: number }
  | { type: "checked"; operation: number; outcome: ReadOutcome }
  | {
      type: "used";
      operation: number;
      key: LangSmithKey;
      remember: ProtectionKind | null;
    }
  | { type: "saved"; operation: number; outcome: SaveOutcome }
  | { type: "unlockStarted"; operation: number }
  | { type: "unlocked"; operation: number; outcome: UnlockOutcome }
  | { type: "forgetStarted"; operation: number }
  | { type: "wiped"; operation: number; outcome: WipeOutcome };

const isUnavailableFailure = (failure: VaultFailure): boolean =>
  failure.kind === "noIndexedDb" || failure.kind === "noWebCrypto";

type Working = Extract<KeyState, { status: "working" }>;

/** The operation in flight, when `operation` is it; an overtaken answer finds null and is dropped. */
const inFlight = (state: KeyState, operation: number): Working | null =>
  state.status === "working" && state.operation === operation ? state : null;

const afterCheck = (outcome: ReadOutcome): KeyState => {
  switch (outcome.status) {
    case "empty":
      return { status: "notSet", notice: null };
    case "remembered":
      return {
        status: "remembered",
        key: outcome.key,
        protection: "device",
      };
    case "locked":
      return { status: "locked", record: outcome.record, wrongAttempts: 0 };
    case "failed":
      return isUnavailableFailure(outcome.failure)
        ? { status: "unavailable", key: null, failure: outcome.failure }
        : { status: "unreadable", failure: outcome.failure };
  }
};

const afterUnlock = (
  unlocking: Extract<Working, { task: "unlocking" }>,
  outcome: UnlockOutcome
): KeyState => {
  switch (outcome.status) {
    case "unlocked":
      return {
        status: "remembered",
        key: outcome.key,
        protection: "passphrase",
      };
    case "wrongPassphrase":
      return {
        status: "locked",
        record: unlocking.record,
        wrongAttempts: unlocking.wrongAttempts + 1,
      };
    case "failed":
      return { status: "unreadable", failure: outcome.failure };
  }
};

/** Every move of the key holder. Null: the move is refused, and nothing changes. */
export const nextKeyState = (
  state: KeyState,
  event: KeyEvent
): KeyState | null => {
  switch (event.type) {
    case "checkStarted":
      return state.status === "unchecked"
        ? { status: "working", task: "checking", operation: event.operation }
        : null;
    case "checked": {
      const working = inFlight(state, event.operation);
      return working?.task === "checking" ? afterCheck(event.outcome) : null;
    }
    case "used":
      // A typed key always wins and decides what the device holds.
      if (state.status === "unavailable") {
        return { ...state, key: event.key };
      }
      return event.remember === null
        ? {
            status: "working",
            task: "keepingInTab",
            operation: event.operation,
            key: event.key,
          }
        : {
            status: "working",
            task: "saving",
            operation: event.operation,
            key: event.key,
            protection: event.remember,
          };
    case "saved": {
      const saving = inFlight(state, event.operation);
      if (saving?.task !== "saving") return null;
      if (event.outcome.status === "saved") {
        return {
          status: "remembered",
          key: saving.key,
          protection: saving.protection,
        };
      }
      return isUnavailableFailure(event.outcome.failure)
        ? {
            status: "unavailable",
            key: saving.key,
            failure: event.outcome.failure,
          }
        : { status: "tabOnly", key: saving.key, notice: event.outcome.failure };
    }
    case "unlockStarted":
      return state.status === "locked"
        ? {
            status: "working",
            task: "unlocking",
            operation: event.operation,
            record: state.record,
            wrongAttempts: state.wrongAttempts,
          }
        : null;
    case "unlocked": {
      const unlocking = inFlight(state, event.operation);
      if (unlocking?.task !== "unlocking") return null;
      return afterUnlock(unlocking, event.outcome);
    }
    case "forgetStarted":
      // Nothing can be stored where storage is unavailable: forgetting is only the tab's copy.
      return state.status === "unavailable"
        ? { ...state, key: null }
        : {
            status: "working",
            task: "forgetting",
            operation: event.operation,
          };
    case "wiped": {
      const wiping = inFlight(state, event.operation);
      const notice =
        event.outcome.status === "wiped" ? null : event.outcome.failure;
      switch (wiping?.task) {
        case "keepingInTab":
          return { status: "tabOnly", key: wiping.key, notice };
        case "forgetting":
          return { status: "notSet", notice };
        default:
          return null;
      }
    }
  }
};

/** The key a request sends in this state, if any. */
const keyInUse = (state: KeyState): LangSmithKey | null => {
  switch (state.status) {
    case "tabOnly":
    case "remembered":
    case "unavailable":
      return state.key;
    case "working":
      return state.task === "saving" || state.task === "keepingInTab"
        ? state.key
        : null;
    case "unchecked":
    case "notSet":
    case "locked":
    case "unreadable":
      return null;
  }
};

// -------------------------------------------- the public view, without key

/** What a component may know: where the key is and what state it is in, never the key. */
export type LangSmithKeyStatus =
  | { status: "checking" }
  | { status: "notSet"; notice: VaultFailure | null }
  | { status: "tabOnly"; notice: VaultFailure | null }
  | { status: "saving"; protection: ProtectionKind }
  | { status: "remembered"; protection: ProtectionKind }
  | { status: "locked"; wrongAttempts: number }
  | { status: "unlocking" }
  | { status: "unreadable"; failure: VaultFailure }
  | { status: "unavailable"; hasKey: boolean; failure: VaultFailure }
  | { status: "forgetting" };

const viewOfWork = (working: Working): LangSmithKeyStatus => {
  switch (working.task) {
    case "checking":
      return { status: "checking" };
    case "forgetting":
      return { status: "forgetting" };
    case "saving":
      return { status: "saving", protection: working.protection };
    case "keepingInTab":
      return { status: "tabOnly", notice: null };
    case "unlocking":
      return { status: "unlocking" };
  }
};

const viewOf = (state: KeyState): LangSmithKeyStatus => {
  switch (state.status) {
    case "unchecked":
      return { status: "checking" };
    case "working":
      return viewOfWork(state);
    case "notSet":
      return { status: "notSet", notice: state.notice };
    case "tabOnly":
      return { status: "tabOnly", notice: state.notice };
    case "remembered":
      return { status: "remembered", protection: state.protection };
    case "locked":
      return { status: "locked", wrongAttempts: state.wrongAttempts };
    case "unreadable":
      return { status: "unreadable", failure: state.failure };
    case "unavailable":
      return {
        status: "unavailable",
        hasKey: state.key !== null,
        failure: state.failure,
      };
  }
};

// ------------------------------------------------- the one mutable holder

let state: KeyState = { status: "unchecked" };
let view: LangSmithKeyStatus = viewOf(state);
let epoch = 0;
let lastOperation = 0;
const listeners = new Set<() => void>();

/** The only place the holder changes. False when the machine refused the move. */
const dispatch = (event: KeyEvent): boolean => {
  const next = nextKeyState(state, event);
  if (next === null) return false;
  if (keyInUse(next) !== keyInUse(state)) epoch += 1;
  state = next;
  view = viewOf(next);
  listeners.forEach((listener) => listener());
  return true;
};

const newOperation = (): number => {
  lastOperation += 1;
  return lastOperation;
};

const normalise = (key: LangSmithKey): LangSmithKey => {
  const workspaceId = key.workspaceId?.trim() ?? "";
  return {
    apiKey: key.apiKey.trim(),
    apiUrl: key.apiUrl,
    workspaceId:
      workspaceId !== "" && isWorkspaceId(workspaceId) ? workspaceId : null,
  };
};

/** Looks for a remembered key, once per tab; later calls do nothing. */
export const loadRememberedLangSmithKey = async (): Promise<void> => {
  const operation = newOperation();
  if (!dispatch({ type: "checkStarted", operation })) return;
  dispatch({ type: "checked", operation, outcome: await readRememberedKey() });
};

/**
 * Uses a typed key at once. With `remember`, it is also stored encrypted on
 * this device; without, any copy on this device is removed. An empty key is a
 * Forget.
 */
export const setLangSmithKey = async (
  typed: LangSmithKey,
  remember: Protection | null = null
): Promise<void> => {
  const key = normalise(typed);
  if (key.apiKey === "") {
    await clearLangSmithKey();
    return;
  }
  const operation = newOperation();
  dispatch({
    type: "used",
    operation,
    key,
    remember: remember === null ? null : remember.kind,
  });
  if (state.status !== "working") return;
  if (remember === null) {
    dispatch({
      type: "wiped",
      operation,
      outcome: await forgetRememberedKey(),
    });
    return;
  }
  dispatch({
    type: "saved",
    operation,
    outcome: await rememberKey(key, remember),
  });
};

/** Unlocks a passphrase-protected key for this tab. */
export const unlockLangSmithKey = async (passphrase: string): Promise<void> => {
  if (state.status !== "locked") return;
  const { record } = state;
  const operation = newOperation();
  if (!dispatch({ type: "unlockStarted", operation })) return;
  dispatch({
    type: "unlocked",
    operation,
    outcome: await unlockRememberedKey(record, passphrase),
  });
};

/**
 * Forget: drops the key from this tab at once, then deletes the device copy
 * (ciphertext, iv, salt and device key). Signing out calls it too, so the next
 * admin on this browser never inherits the key.
 */
export const clearLangSmithKey = async (): Promise<void> => {
  const operation = newOperation();
  dispatch({ type: "forgetStarted", operation });
  if (state.status !== "working") return;
  dispatch({ type: "wiped", operation, outcome: await forgetRememberedKey() });
};

/** The headers a LangSmith-backed request carries: none without a key (cerebro then uses its own). */
export const langsmithHeaders = (): Record<string, string> => {
  const key = keyInUse(state);
  if (key === null) return {};
  return {
    "X-LangSmith-Api-Key": key.apiKey,
    ...(key.apiUrl === null ? {} : { "X-LangSmith-Api-Url": key.apiUrl }),
    ...(key.workspaceId === null
      ? {}
      : { "X-LangSmith-Workspace-Id": key.workspaceId }),
  };
};

/** Replaces the key in use, wherever it appears in `text`, so error reporting never carries it. */
export const redactLangSmithKey = (text: string): string => {
  const key = keyInUse(state);
  return key === null || key.apiKey.length < 8
    ? text
    : text.split(key.apiKey).join("[LangSmith key removed]");
};

/** Calls `listener` after every change of the key's state; returns the unsubscribe. */
export const subscribeToLangSmithKey = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Where the key is and what state it is in, outside React (the same value the hook gives). */
export const getLangSmithKeyStatus = (): LangSmithKeyStatus => view;

/** Changes exactly when the key a request sends changes. */
export const getLangSmithKeyEpoch = (): number => epoch;

/** Where the key is and what state it is in, for the status line. */
export const useLangSmithKeyStatus = (): LangSmithKeyStatus =>
  useSyncExternalStore(subscribeToLangSmithKey, getLangSmithKeyStatus);

/** Where the next search reads, in words: the region and workspace (neither is secret). */
export const useLangSmithTarget = (): {
  region: "US" | "EU" | null;
  workspaceId: string | null;
} => {
  const target = useSyncExternalStore(subscribeToLangSmithKey, () =>
    targetKey()
  );
  const [region, workspaceId] = target.split("|");
  return {
    region: region === "US" || region === "EU" ? region : null,
    workspaceId:
      workspaceId === undefined || workspaceId === "" ? null : workspaceId,
  };
};

const targetKey = (): string => {
  const key = keyInUse(state);
  return key === null
    ? "|"
    : `${key.apiUrl === LANGSMITH_HOSTS[0] ? "US" : key.apiUrl === LANGSMITH_HOSTS[1] ? "EU" : ""}|${key.workspaceId ?? ""}`;
};

/** Whether a key is in use, and its epoch for request arguments. The key itself is never handed to a component. */
export const useLangSmithKeyState = (): { hasKey: boolean; epoch: number } => {
  const hasKey = useSyncExternalStore(
    subscribeToLangSmithKey,
    () => keyInUse(state) !== null
  );
  const keyEpoch = useSyncExternalStore(
    subscribeToLangSmithKey,
    getLangSmithKeyEpoch
  );
  return { hasKey, epoch: keyEpoch };
};
